import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';
import { RevisionPage } from './pages/RevisionPage';
import { AppointmentPage } from './pages/AppointmentPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Travelpayouts "Drive" (afiliados de viaje). El script de un tercero se carga SOLO en la
// portada, donde está la franja "Planee su viaje". Nunca debe correr en las páginas donde el
// cliente escribe datos personales (pedido, pago, foto, revisión, gracias). Como el sitio es
// una sola página (SPA), un script ya cargado no se puede descargar; por eso, si el cliente
// pasa de la portada a una página sensible, se recarga la página completa para empezar
// limpia y sin el script.
const TP_SRC = 'https://emrld.ltd/NTg3MDcx.js?t=587071';
const TP_ALLOWED = ['/'];
const TP_SENSITIVE = ['/pedido', '/gracias', '/foto', '/revision', '/appointment'];
let tpLoaded = false;
function TravelpayoutsLoader() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    if (tpLoaded && TP_SENSITIVE.includes(path)) {
      window.location.reload();
      return;
    }
    if (!tpLoaded && TP_ALLOWED.includes(path)) {
      try {
        const s = document.createElement('script');
        s.async = true;
        s.setAttribute('data-cmp-ab', '2');
        s.src = TP_SRC;
        document.head.appendChild(s);
        tpLoaded = true;
      } catch {
        /* sin el script el sitio funciona igual */
      }
    }
  }, [pathname]);
  return null;
}

// Cada página pública tiene su propio título ("Precios | Noor Passport Services").
// Así Google puede mostrar cada página con un nombre claro debajo del resultado de
// la marca. La portada y las páginas internas muestran solo "Noor Passport Services".
const BRAND = 'Noor Passport Services';
const PAGE_TITLES: Record<string, Record<Language, string>> = {
  '/how-it-works': {
    en: 'How it works',
    es: 'Cómo funciona',
    ht: 'Kijan li mache',
    vi: 'Cách hoạt động'
  },
  '/pricing': {
    en: 'Packages & pricing',
    es: 'Paquetes y precios',
    ht: 'Pakè ak pri',
    vi: 'Gói và bảng giá'
  },
  '/faq': {
    en: 'Frequently asked questions',
    es: 'Preguntas frecuentes',
    ht: 'Kesyon moun poze souvan',
    vi: 'Câu hỏi thường gặp'
  },
  '/contact': {
    en: 'Contact',
    es: 'Contacto',
    ht: 'Kontak',
    vi: 'Liên hệ'
  },
  '/guia': {
    en: 'Which form do I need?',
    es: '¿Qué formulario necesito?',
    ht: 'Ki fòm mwen bezwen?',
    vi: 'Tôi cần mẫu đơn nào?'
  },
  '/privacy-policy': {
    en: 'Privacy policy',
    es: 'Política de privacidad',
    ht: 'Politik vi prive',
    vi: 'Chính sách quyền riêng tư'
  },
  '/terms-of-service': {
    en: 'Terms of service',
    es: 'Términos de servicio',
    ht: 'Kondisyon sevís',
    vi: 'Điều khoản dịch vụ'
  },
  '/refund-policy': {
    en: 'Refund policy',
    es: 'Política de reembolsos',
    ht: 'Politik ranbousman',
    vi: 'Chính sách hoàn tiền'
  }
};
function PageTitle() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const key = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const p = PAGE_TITLES[key];
    document.title = p ? `${p[language]} | ${BRAND}` : BRAND;
  }, [pathname, language]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="1w4yj9ynknpyg">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="2ev6gy18i5o79">
      <BrowserRouter data-id="element-2" data-mp="2etv5df8tfc5l">
        <ScrollToTop data-id="element-3" data-mp="2euelcqewnqgz" />
        <PageTitle data-id="element-4" data-mp="2ex97cmdkhjci" />
        <TravelpayoutsLoader data-id="element-5" data-mp="2jtr8lc9ouzk3" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-6" data-mp="2jsybz44g1qfo">
          <SkipLink data-id="element-7" data-mp="2nwnyzp5gzzyl" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-8" data-mp="tjehpb6ona5s" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-9" data-mp="yir640d7cpah">
            <Header data-id="element-10" data-mp="21qsjgb7642hs" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-11" data-mp="21s3w70ilckl1">
            <Routes data-id="element-12" data-mp="2as8vendmsco4">
              <Route path="/" element={<Home data-id="element-14" data-mp="2at1s0vivllsj" />} data-id="element-13" data-mp="2asimkeaewpve" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-16" data-mp="25tfiox664pp0" />} data-id="element-15" data-mp="2aq5u37712x43" />
              <Route path="/pricing" element={<PricingPage data-id="element-18" data-mp="25v9zbzaepvna" />} data-id="element-17" data-mp="25se6gpdf1x5p" />
              <Route path="/faq" element={<FAQPage data-id="element-20" data-mp="2kp568zr05avu" />} data-id="element-19" data-mp="2kpwprc7bri5r" />
              <Route path="/contact" element={<Contact data-id="element-22" data-mp="2frlfx1h157nj" />} data-id="element-21" data-mp="2kobvlnoxa18f" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-24" data-mp="2fp91uzee7hfr" />} data-id="element-23" data-mp="2fqae3775a9z2" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-26" data-mp="2tkktgxx94rsg" />} data-id="element-25" data-mp="2tj9r92jgfs3g" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-28" data-mp="2pj9cfgsg1evv" />} data-id="element-27" data-mp="2pi7tkq56ro3l" />
              <Route path="/gracias" element={<GraciasPage data-id="element-30" data-mp="33hpgt12u2tau" />} data-id="element-29" data-mp="33gehbdgf87pj" />
              <Route path="/guia" element={<GuiaPage data-id="element-32" data-mp="2yicymtmwqael" />} data-id="element-31" data-mp="33el75o6ln5cs" />
              <Route path="/pedido" element={<PedidoPage data-id="element-34" data-mp="2yfqg5yyhaahl" />} data-id="element-33" data-mp="2yj5vn2w2dljj" />
              <Route path="/foto" element={<FotoPage data-id="element-36" data-mp="3i4oaz3t2s1t9" />} data-id="element-35" data-mp="3i3welqrnm01x" />
              <Route path="/revision" element={<RevisionPage data-id="element-38" data-mp="3n2pu1oqi414q" />} data-id="element-37" data-mp="3i4ef4zlcn0fn" />
              <Route path="/appointment" element={<AppointmentPage data-id="element-40" data-mp="3n0n3n3lfs5zh" />} data-id="element-39" data-mp="3n1oh1epxd4kd" />
            </Routes>
          </main>
          <Footer data-id="element-41" data-mp="384nnd5o6azj9" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-42" data-mp="3d3qt6sbz99sa" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}